// Form-value-to-translation-key mapping (Phase 3.B — 2026-06-24).
//
// The forms keep Arabic VALUES in their dropdowns (so the backend
// continues to receive Arabic strings — zero ship-breaking risk for old
// mobile clients). This helper provides a single source of truth for
// mapping each Arabic value to a translation key under the `formValues.*`
// namespace, so all forms render their dropdowns in the user's language
// while still POSTing Arabic.
//
// Usage in a component (with useTranslation):
//   const { t } = useTranslation();
//   <SelectItem value={value}>{getFormValueLabel(value, t)}</SelectItem>
//
// Rendering a woman's OWN answers (bride card / bride profile) — pass the
// third argument so the Arabic label comes out feminine:
//   getFormValueLabel(religiosityLevel, t, true)  // 'ملتزم' → 'ملتزمة'
// Do NOT pass it for partner-requirement values: those describe the other
// side, so they keep the gender of the stored value.
//
// Adding a new mapping:
//   1. Add the key to frontend/src/locales/ar.json + en.json under
//      "formValues" (Arabic value stays in ar.json so AR users still see
//      the original string; English label goes in en.json).
//   2. Add an entry to the VALUE_TO_KEY map below.
//
// Why a single helper rather than per-page maps:
//   Several pages (BrideFormPage, GroomFormPage, ProfileEditPage, plus
//   any future read-only display pages) share the same value vocabulary.
//   Centralising avoids drift.

import { COUNTRY_NAMES } from '../data/countryNames';
import i18n from '../i18n/config';

const VALUE_TO_KEY = {
  // Nationalities — the stored value stays Arabic; only the label
  // changes. Ported from mobile's form_value_labels.dart so both
  // platforms say the same word for the same value.
  'مصري': 'natEgyptian',
  'سعودي': 'natSaudi',
  'إماراتي': 'natEmirati',
  'كويتي': 'natKuwaiti',
  'قطري': 'natQatari',
  'عماني': 'natOmani',
  'بحريني': 'natBahraini',
  'أردني': 'natJordanian',
  'سوري': 'natSyrian',
  'فلسطيني': 'natPalestinian',
  'لبناني': 'natLebanese',
  'عراقي': 'natIraqi',
  'يمني': 'natYemeni',
  'سوداني': 'natSudanese',
  'ليبي': 'natLibyan',
  'تونسي': 'natTunisian',
  'جزائري': 'natAlgerian',
  'مغربي': 'natMoroccan',
  'موريتاني': 'natMauritanian',
  'جيبوتي': 'natDjiboutian',
  'قمري': 'natComorian',
  'تركي': 'natTurkish',
  'إيراني': 'natIranian',
  'أفغاني': 'natAfghan',
  'باكستاني': 'natPakistani',
  'هندي': 'natIndian',
  'بنغلاديشي': 'natBangladeshi',
  'إندونيسي': 'natIndonesian',
  'ماليزي': 'natMalaysian',
  'أوزبكي': 'natUzbek',
  'كازاخي': 'natKazakh',
  'أذربيجاني': 'natAzerbaijani',
  'صومالي': 'natSomali',
  'نيجيري': 'natNigerian',
  'سنغالي': 'natSenegalese',
  'تشادي': 'natChadian',
  'بوسني': 'natBosnian',
  'ألباني': 'natAlbanian',
  'كوسوفي': 'natKosovar',
  'بريطاني': 'natBritish',
  'فرنسي': 'natFrench',
  'ألماني': 'natGerman',
  'أمريكي': 'natAmerican',
  'كندي': 'natCanadian',
  'أسترالي': 'natAustralian',
  'هولندي': 'natDutch',
  'بلجيكي': 'natBelgian',
  'سويدي': 'natSwedish',
  'إسباني': 'natSpanish',
  'إيطالي': 'natItalian',
  'أخرى': 'natOther',
  // Skin tones
  'أبيض': 'skinWhite',
  'أبيض مائل للقمحي': 'skinWhiteWheatish',
  'قمحي فاتح': 'skinWheatishLight',
  'قمحي': 'skinWheatish',
  // The feminine of أبيض, held by a live profile. Without a row it rendered
  // as raw Arabic in every other language.
  'بيضاء': 'skinWhite',
  'حنطي': 'skinWheatishAlt',
  'قمحي غامق': 'skinWheatishDark',
  'أسمر فاتح': 'skinTanLight',
  'أسمر': 'skinTan',
  'أسمر غامق': 'skinTanDark',

  // Body types
  'نحيف': 'bodyThin',
  'رياضي': 'bodyAthletic',
  'متوسط': 'bodyMedium',
  'ممتلئ': 'bodyOverweight',
  'بدين': 'bodyHeavy',
  'أي': 'anyPlaceholder',

  // Education
  'ثانوي': 'eduHighSchool',
  'دبلوم': 'eduDiploma',
  'بكالوريوس': 'eduBachelor',
  'ماجستير': 'eduMaster',
  'دكتوراه': 'eduDoctorate',

  // Job statuses (gendered Arabic, English ungendered)
  'موظف حكومي': 'workGovernment',
  'موظف قطاع خاص': 'workPrivate',
  'أعمال حرة': 'workFreelance',
  'صاحب عمل': 'workEmployer',
  'طالب': 'workStudent',
  'لا يعمل': 'workNone',
  'موظفة': 'workEmployedFemale',
  'موظفة حكومي': 'workGovernment',
  'موظفة قطاع خاص': 'workPrivate',
  'ربة منزل': 'workHomemaker',
  'غير عاملة': 'workUnemployedFemale',
  'لا تعمل': 'workUnemployedFemale',
  'طالبة': 'workStudentFemale',

  // Marital statuses
  'أعزب': 'maritalSingleMale',
  'مطلق': 'maritalDivorcedMale',
  'أرمل': 'maritalWidowerMale',
  'متزوج': 'maritalMarriedMale',
  // آنسة is the same status as عزباء — the moderation spec treats them as one
  // (INV-FORM-21) — and only عزباء was mapped, so a bride who picked آنسة
  // rendered as raw Arabic inside an English, French or German sentence.
  // Same gap existed on mobile.
  'آنسة': 'maritalSingleFemale',
  'عزباء': 'maritalSingleFemale',
  'مطلقة': 'maritalDivorcedFemale',
  'أرملة': 'maritalWidowFemale',

  // Income / family levels
  'منخفض': 'incomeLow',
  'فوق المتوسط': 'incomeAboveMedium',
  'مرتفع': 'incomeHigh',
  'ميسور': 'incomeWealthy',
  'بسيطة': 'familySimple',
  'متوسطة': 'familyMedium',
  'مرتفعة': 'familyHigh',

  // Children / wants children
  'نعم': 'childrenYes',
  'لا': 'childrenNo',
  'حسب الظروف': 'childrenDepends',
  'حسب العدد': 'childrenByCount',

  // Custody
  'معي': 'custodyWithMe',
  'مع الأم': 'custodyWithMother',
  'مشتركة': 'custodyShared',

  // Area types / neighborhoods
  'شعبي': 'neighborhoodPopular',
  'راقي': 'neighborhoodUpscale',
  'فيلا': 'housingVilla',
  'شقة إيجار': 'housingApartmentRented',
  'شقة تمليك': 'housingApartmentOwned',
  'مع الأسرة': 'housingWithFamily',

  // Religiosity (gendered Arabic)
  'غير ملتزم': 'religiosityNone',
  'غير ملتزمة': 'religiosityNone',
  'ملتزم': 'religiosityCommitted',
  'ملتزمة': 'religiosityCommitted',
  'ملتزم جداً': 'religiosityVeryCommitted',
  'ملتزمة جداً': 'religiosityVeryCommitted',

  // Prayer
  'غير منتظم': 'prayerRare',
  'أحيانًا': 'prayerSometimes',
  'غالبًا': 'prayerOften',
  'منتظم دائمًا': 'prayerAlways',
  'أغلب الأوقات': 'prayerMostOfTime',
  'نادرًا': 'prayerRarely',

  // Hijab types
  'طرحة': 'hijabTarha',
  'خمار': 'hijabKhimar',
  'نقاب': 'hijabNiqab',
  'منقبة': 'hijabNiqab',
  'منتقبة': 'hijabNiqabAlt',
  'محجبة': 'hijabHeadscarf',
  'غير محجبة': 'hijabNone',

  // Beard
  'لا يوجد': 'beardNonePlain',
  'لحية خفيفة': 'beardLight',
  'لحية كاملة': 'beardFull',
  'بدون لحية': 'beardNone',
  'لا يشترط': 'noPreference',

  // Smoking
  'لا أدخن': 'smokingNoneAlt',
  'أحياناً': 'smokingSometimes',
  'مدخن': 'smokingYes',
  'أدخن': 'smokingYesAlt',
  'أقلعت عن التدخين': 'smokingQuit',

  // Personality traits (male forms)
  'هادئ': 'traitCalm',
  'اجتماعي': 'traitSocial',
  'طموح': 'traitAmbitious',
  'رومانسي': 'traitRomantic',
  'جاد': 'traitSerious',
  'مرح': 'traitCheerful',
  'مثقف': 'traitCultured',
  'عملي': 'traitPractical',

  // Personality traits (female forms — bride form). They name their OWN
  // feminine labels: every bride stores the feminine word, and mapping it to
  // the masculine key described her as «طموح» / "Ambitieux" on her own form
  // and on every man's view of her profile.
  'هادئة': 'traitCalmFemale',
  'اجتماعية': 'traitSocialFemale',
  'طموحة': 'traitAmbitiousFemale',
  'رومانسية': 'traitRomanticFemale',
  'جدية': 'traitSeriousFemale',
  'مرحة': 'traitCheerfulFemale',
  'مثقفة': 'traitCulturedFemale',
  'عملية': 'traitPracticalFemale',
  'متواضعة': 'traitHumbleFemale',
  'متدينة': 'traitReligiousFemale',

  // Interest chips (owner, 2026-10-30): the stored word, labelled in the reader's language.
  'القراءة': 'interestReading',
  'الطبخ': 'interestCooking',
  'السفر': 'interestTravel',
  'الرياضة': 'interestSports',
  'المشي': 'interestWalking',
  'السباحة': 'interestSwimming',
  'كرة القدم': 'interestFootball',
  'الرسم': 'interestDrawing',
  'التصوير': 'interestPhotography',
  'الخط العربي': 'interestCalligraphy',
  'حفظ القرآن': 'interestQuran',
  'العمل التطوعي': 'interestVolunteering',
  'التقنية': 'interestTechnology',
  'الزراعة': 'interestGardening',
  'الخياطة والتطريز': 'interestSewing',
  'ركوب الخيل': 'interestHorseRiding',

  // Partner special conditions (bride form)
  'غير مدخن': 'conditionNonSmoker',
  'ملتزم دينياً': 'conditionReligious',
  'غير متزوج سابقاً': 'conditionNeverMarried',
  'بدون أطفال': 'conditionNoChildren',

  // Health status
  'سليم': 'healthHealthyMale',
  'سليمة': 'healthHealthyFemale',
  'إعاقة بسيطة': 'healthDisabilityMild',
  'إعاقة متوسطة': 'healthDisabilityModerate',
  'إعاقة شديدة': 'healthDisabilitySevere',
  'مرض مزمن': 'healthChronicDisease',
  'حالة خاصة': 'healthSpecialCase',

  // Acceptance levels
  'لا أعرف': 'acceptanceDontKnow',

  // Countries of residence
  'مصر': 'countryEgypt',
  'السعودية': 'countrySaudiArabia',
  'الإمارات': 'countryUAE',
  'الكويت': 'countryKuwait',
  'قطر': 'countryQatar',
  'البحرين': 'countryBahrain',
  'عُمان': 'countryOman',
  'الأردن': 'countryJordan',
  'لبنان': 'countryLebanon',
  'سوريا': 'countrySyria',
  'العراق': 'countryIraq',
  'فلسطين': 'countryPalestine',
  'اليمن': 'countryYemen',
  'السودان': 'countrySudan',
  'ليبيا': 'countryLibya',
  'تونس': 'countryTunisia',
  'الجزائر': 'countryAlgeria',
  'المغرب': 'countryMorocco',
  'تركيا': 'countryTurkey',
  'ألمانيا': 'countryGermany',
  'فرنسا': 'countryFrance',
  'بريطانيا': 'countryUK',
  'أمريكا': 'countryUSA',
  'كندا': 'countryCanada',
  'أستراليا': 'countryAustralia',
  'أخرى': 'countryOther',

  // Egyptian governorates
  'القاهرة': 'cityQahira',
  'الجيزة': 'cityGiza',
  'الإسكندرية': 'cityAlexandria',
  'الدقهلية': 'cityDaqahlia',
  'البحر الأحمر': 'cityRedSea',
  'البحيرة': 'cityBeheira',
  'الفيوم': 'cityFayyum',
  'الغربية': 'cityGharbia',
  'الإسماعيلية': 'cityIsmailia',
  'المنوفية': 'cityMonufia',
  'المنيا': 'cityMianya',
  'القليوبية': 'cityQalyubia',
  'الوادي الجديد': 'cityNewValley',
  'السويس': 'citySuez',
  'أسوان': 'cityAswan',
  'أسيوط': 'cityAssiut',
  'بني سويف': 'cityBeniSuef',
  'بورسعيد': 'cityPortSaid',
  'دمياط': 'cityDamietta',
  'الشرقية': 'citySharqia',
  'جنوب سيناء': 'citySouthSinai',
  'كفر الشيخ': 'cityKafrElSheikh',
  'مطروح': 'cityMatrouh',
  'الأقصر': 'cityLuxor',
  'قنا': 'cityQena',
  'شمال سيناء': 'cityNorthSinai',
  'سوهاج': 'citySohag',

  // Saudi cities
  'الرياض': 'cityRiyadh',
  'جدة': 'cityJeddah',
  'مكة المكرمة': 'cityMakkah',
  'المدينة المنورة': 'cityMadinah',
  'الدمام': 'cityDammam',
  'الخبر': 'cityKhobar',
  'الطائف': 'cityTaif',
  'تبوك': 'cityTabuk',
  'بريدة': 'cityBuraydah',
  'خميس مشيط': 'cityKhamisMushait',
  'أبها': 'cityAbha',
  'حائل': 'cityHail',
  'نجران': 'cityNajran',
  'الجبيل': 'cityJubail',
  'ينبع': 'cityYanbu',
  'الأحساء': 'cityAlAhsa',
  'جازان': 'cityJizan',
  'القطيف': 'cityQatif',
  'عرعر': 'cityArar',
  'سكاكا': 'citySakaka',

  // UAE cities
  'دبي': 'cityDubai',
  'أبوظبي': 'cityAbuDhabi',
  'الشارقة': 'citySharjah',
  'العين': 'cityAlAin',
  'عجمان': 'cityAjman',
  'رأس الخيمة': 'cityRasAlKhaimah',
  'الفجيرة': 'cityFujairah',
  'أم القيوين': 'cityUmmAlQuwain',

  // Kuwait cities
  'العاصمة': 'cityKuwaitCity',
  'حولي': 'cityHawalli',
  'الفروانية': 'cityFarwaniya',
  'مبارك الكبير': 'cityMubarakAlKabeer',
  'الأحمدي': 'cityAhmadi',
  'الجهراء': 'cityJahra',

  // Qatar cities
  'الدوحة': 'cityDoha',
  'الريان': 'cityRayyan',
  'الوكرة': 'cityWakra',
  'الخور': 'cityAlKhor',
  'أم صلال': 'cityUmmSalal',
  'الشمال': 'cityAlShamal',
  'الضعاين': 'cityAlDaayen',

  // Bahrain cities
  'المنامة': 'cityManama',
  'المحرق': 'cityMuharraq',
  'الرفاع': 'cityRiffa',
  'مدينة حمد': 'cityHamadTown',
  'مدينة عيسى': 'cityIsaTown',
  'سترة': 'citySitra',
  'الحد': 'cityHidd',

  // Oman cities
  'مسقط': 'cityMuscat',
  'صلالة': 'citySalalah',
  'صحار': 'citySohar',
  'نزوى': 'cityNizwa',
  'السيب': 'citySeeb',
  'بركاء': 'cityBarka',
  // 'صور' (Sur, Oman) is identical to the Lebanese city Tyre — omitted here to
  // avoid collision; the Lebanon entry below maps 'صور' → cityTyre. Sur (Oman)
  // falls back to the raw Arabic value which is acceptable.
  'البريمي': 'cityAlBuraimi',

  // Jordan cities
  'عمّان': 'cityAmman',
  'إربد': 'cityIrbid',
  'الزرقاء': 'cityZarqa',
  'العقبة': 'cityAqaba',
  'السلط': 'citySalt',
  'المفرق': 'cityMafraq',
  'مادبا': 'cityMadaba',
  'الكرك': 'cityKarak',

  // Lebanon cities
  'بيروت': 'cityBeirut',
  'طرابلس': 'cityTripoli',
  'صيدا': 'citySidon',
  'صور': 'cityTyre',
  'زحلة': 'cityZahle',
  'جونية': 'cityJounieh',
  'بعلبك': 'cityBaalbek',

  // Syria cities
  'دمشق': 'cityDamascus',
  'حلب': 'cityAleppo',
  'حمص': 'cityHoms',
  'اللاذقية': 'cityLatakia',
  'حماة': 'cityHama',
  'طرطوس': 'cityTartus',
  'دير الزور': 'cityDeirEzzor',

  // Iraq cities
  'بغداد': 'cityBaghdad',
  'البصرة': 'cityBasra',
  'الموصل': 'cityMosul',
  'أربيل': 'cityErbil',
  'النجف': 'cityNajaf',
  'كربلاء': 'cityKarbala',
  'كركوك': 'cityKirkuk',
  'السليمانية': 'citySulaymaniyah',
  'الناصرية': 'cityNasiriyah',

  // Palestine cities
  'القدس': 'cityJerusalem',
  'غزة': 'cityGaza',
  'رام الله': 'cityRamallah',
  'نابلس': 'cityNablus',
  'الخليل': 'cityHebron',
  'بيت لحم': 'cityBethlehem',
  'جنين': 'cityJenin',

  // Yemen cities
  'صنعاء': 'citySanaa',
  'عدن': 'cityAden',
  'تعز': 'cityTaiz',
  'الحديدة': 'cityHodeidah',
  'إب': 'cityIbb',
  'المكلا': 'cityMukalla',
  'ذمار': 'cityDhamar',

  // Sudan cities
  'الخرطوم': 'cityKhartoum',
  'أم درمان': 'cityOmdurman',
  'بحري': 'cityBahri',
  'بورتسودان': 'cityPortSudan',
  'كسلا': 'cityKassala',
  'الأبيض': 'cityElObeid',
  'نيالا': 'cityNyala',
  'ود مدني': 'cityWadMadani',

  // Libya cities
  // 'طرابلس' (Tripoli, Libya) is also the Lebanon city name — the Lebanon entry
  // already maps it to cityTripoli → "Tripoli", which is correct for Libya too.
  'بنغازي': 'cityBenghazi',
  'مصراتة': 'cityMisrata',
  'الزاوية': 'cityZawiya',
  'البيضاء': 'cityBayda',
  'سبها': 'citySabha',
  'طبرق': 'cityTobruk',

  // Tunisia cities
  // 'تونس' (Tunis city) is the same Arabic as the country 'countryTunisia'.
  // The country entry above takes precedence — in the city context the Arabic
  // value already reads naturally and the country label "Tunisia" is acceptable.
  'صفاقس': 'citySfax',
  'سوسة': 'citySousse',
  'القيروان': 'cityKairouan',
  'بنزرت': 'cityBizerte',
  'قابس': 'cityGabes',
  'أريانة': 'cityAriana',

  // Algeria cities
  // 'الجزائر' (Algiers city) is the same Arabic as the country 'countryAlgeria'.
  // Country entry takes precedence. The Arabic value remains readable as-is in
  // city context.
  'وهران': 'cityOran',
  'قسنطينة': 'cityConstantine',
  'عنابة': 'cityAnnaba',
  'البليدة': 'cityBlida',
  'باتنة': 'cityBatna',
  'سطيف': 'citySetif',

  // Morocco cities
  'الدار البيضاء': 'cityCasablanca',
  'الرباط': 'cityRabat',
  'فاس': 'cityFes',
  'مراكش': 'cityMarrakech',
  'طنجة': 'cityTangier',
  'أكادير': 'cityAgadir',
  'مكناس': 'cityMeknes',
  'وجدة': 'cityOujda',

  // Turkey cities
  'إسطنبول': 'cityIstanbul',
  'أنقرة': 'cityAnkara',
  'إزمير': 'cityIzmir',
  'بورصة': 'cityBursa',
  'أنطاليا': 'cityAntalya',
  'أضنة': 'cityAdana',
  'غازي عنتاب': 'cityGaziantep',
  'قونية': 'cityKonya',

  // Germany cities (added 2026-07-04 for diaspora audience)
  'برلين': 'cityBerlin',
  'ميونخ': 'cityMunich',
  'هامبورغ': 'cityHamburg',
  'فرانكفورت': 'cityFrankfurt',
  'كولونيا': 'cityCologne',
  'شتوتغارت': 'cityStuttgart',
  'دوسلدورف': 'cityDusseldorf',
  'بون': 'cityBonn',
  'بريمن': 'cityBremen',
  'هانوفر': 'cityHannover',
  'دورتموند': 'cityDortmund',
  'دويسبورغ': 'cityDuisburg',

  // France cities (added 2026-07-04)
  'باريس': 'cityParis',
  'مارسيليا': 'cityMarseille',
  'ليون': 'cityLyon',
  'تولوز': 'cityToulouse',
  'نيس': 'cityNice',
  'ستراسبورغ': 'cityStrasbourg',
  'ليل': 'cityLille',
  'بوردو': 'cityBordeaux',
  'نانت': 'cityNantes',
  'مونبلييه': 'cityMontpellier',

  // UK cities (added 2026-07-04)
  // 'طرابلس' (Lebanon/Libya) already maps to cityTripoli — UK 'ليدز' Leeds is
  // unambiguous. No collisions with existing entries.
  'لندن': 'cityLondon',
  'برمنغهام': 'cityBirmingham',
  'مانشستر': 'cityManchester',
  'ليدز': 'cityLeeds',
  'غلاسكو': 'cityGlasgow',
  'ليفربول': 'cityLiverpool',
  'ليستر': 'cityLeicester',
  'برادفورد': 'cityBradford',
  'أدنبرة': 'cityEdinburgh',
  'بريستول': 'cityBristol',
  'شفيلد': 'citySheffield',

  // USA cities (added 2026-07-04)
  'نيويورك': 'cityNewYork',
  'لوس أنجلوس': 'cityLosAngeles',
  'شيكاغو': 'cityChicago',
  'هيوستن': 'cityHouston',
  'دالاس': 'cityDallas',
  'ديترويت': 'cityDetroit',
  'واشنطن العاصمة': 'cityWashingtonDC',
  'بوسطن': 'cityBoston',
  'ميناوبوليس': 'cityMinneapolis',
  'سان فرانسيسكو': 'citySanFrancisco',
  'فيلادلفيا': 'cityPhiladelphia',
  'أتلانتا': 'cityAtlanta',
  'سياتل': 'citySeattle',

  // Canada cities (added 2026-07-04)
  'تورونتو': 'cityToronto',
  'مونتريال': 'cityMontreal',
  'فانكوفر': 'cityVancouver',
  'كالغاري': 'cityCalgary',
  'أوتاوا': 'cityOttawa',
  'إدمنتون': 'cityEdmonton',
  'ميسيساوغا': 'cityMississauga',
  'وينيبيغ': 'cityWinnipeg',
  'هاميلتون': 'cityHamilton',

  // Australia cities (added 2026-07-04)
  'سيدني': 'citySydney',
  'ملبورن': 'cityMelbourne',
  'بريسبان': 'cityBrisbane',
  'بيرث': 'cityPerth',
  'أديلايد': 'cityAdelaide',
  'غولد كوست': 'cityGoldCoast',
  // Aligned with the other platform's map + spellings stored by older clients (2026-10-15); a test keeps the two maps equal.
  'ابتدائي': 'primary',
  'إعدادي': 'preparatory',
  'متوسط التدين': 'moderatelyReligious',
  'متزوجة': 'married',
  'محدود': 'limited',
  'جيد': 'good',
  'جيد جداً': 'veryGood',
  'ممتاز': 'excellent',
  'موظف': 'employed',
  'عمل حر': 'selfEmployed',
  'متقاعد': 'retired',
  'متقاعدة': 'retired2',
  'فاتح': 'fair',
  'داكن': 'dark',
  'منتظم': 'regular',
  'نادراً': 'rarely',
  'أب': 'father',
  'أخ': 'brother',
  'عم': 'paternalUncle',
  'خال': 'maternalUncle',
  'ابن العم': 'paternalCousin',
  'ابن الخال': 'maternalCousin',
  'جد': 'grandfather',
  'أم': 'mother',
  'عم-خال': 'uncle',
  'ولي أمر': 'guardian',
  'أسرة': 'family',
  'إعاقة': 'healthDisability',
  'سيئة': 'healthPoor',
  'حضانة مشتركة': 'custodyShared',
  'دائماً': 'prayerAlways',
  'دائما': 'prayerAlways',
  'اعزب': 'maritalSingleMale',
  'لا يدخن': 'conditionNonSmoker',
  'لا تدخن': 'conditionNonSmoker',
};

// Keys that the map above deliberately collapses across BOTH Arabic genders
// (e.g. 'ملتزم' and 'ملتزمة' both → religiosityCommitted), where the shared
// label is the masculine one. Each of these has a `<key>Female` sibling in
// ar.json/en.json holding the feminine Arabic wording — the same Male/Female
// key convention already used by maritalSingle*, healthHealthy* and work*Female.
const KEYS_WITH_FEMININE_LABEL = new Set([
  'religiosityNone',
  'religiosityCommitted',
  'religiosityVeryCommitted',
  'workGovernment',
  'workPrivate',
  'traitCalm',
  'traitSocial',
  'traitAmbitious',
  'traitRomantic',
  'traitSerious',
  'traitCheerful',
  'traitCultured',
  'traitPractical',
  'traitHumble',
  'traitReligious',
]);

/**
 * Translate a dropdown VALUE string to its localized label.
 *
 * @param {string} value    the Arabic value as stored (never modified)
 * @param {Function} t      i18next translate
 * @param {boolean} isFemale render the feminine Arabic label when one exists
 *                           (the person being described is a woman)
 *
 * Falls back to the masculine label, then to the original value (Arabic), if
 * no mapping or translation exists.
 */
export function getFormValueLabel(value, t, isFemale = false) {
  if (!value) return '';
  const key = VALUE_TO_KEY[value];
  if (!key) {
    // Countries of residence are GENERATED from CLDR (see
    // data/countryNames.js), not hand-mapped: there are 245 of them and
    // hand-typing four languages each is how a name ends up mistyped and
    // silently unmatchable (BUG-250/251).
    //
    // Checked AFTER the hand map on purpose. The 25 countries the app shipped
    // with have curated keys — "بريطانيا" reads better than CLDR's
    // "المملكة المتحدة" — and those keep winning, so nothing a member already
    // sees changes spelling.
    const country = COUNTRY_NAMES[value];
    if (country) {
      const lang = (i18n.resolvedLanguage || i18n.language || 'ar').slice(0, 2);
      return country[lang] || country.ar || value;
    }
    return value;
  }
  const localizedKey = isFemale && KEYS_WITH_FEMININE_LABEL.has(key) ? `${key}Female` : key;
  const translated = t(`formValues.${localizedKey}`);
  // If the i18n returned the literal key path, the key was missing — fall
  // back to the base (masculine) key, then to the original Arabic value,
  // rather than rendering a debug string in the UI.
  if (!translated.startsWith('formValues.')) return translated;
  if (localizedKey !== key) {
    const base = t(`formValues.${key}`);
    if (!base.startsWith('formValues.')) return base;
  }
  return value;
}

// `health_status` and `wants_children` hold the Arabic values the forms write
// («سليم», «نعم», «حسب الظروف») AND the English codes an early client wrote
// ('good', 'yes', 'maybe'). Profile details compared the English codes only,
// so 6,427 of 6,434 live profiles read "-" for wanting children and 5,335
// showed «سليم» untranslated on an English page (2026-10-30). One answer for
// both spellings, used by every screen that shows them.
const LEGACY_HEALTH = { good: 'healthGood', medium: 'healthMedium', poor: 'healthPoor', chronic: 'healthChronic', disability: 'healthDisability' };
const LEGACY_WANTS_CHILDREN = { yes: 'childrenYes', no: 'childrenNo', maybe: 'childrenDepends' };

export function healthLabel(value, t, isFemale = false) {
  if (!value) return '';
  const legacy = LEGACY_HEALTH[value];
  return legacy ? t(`formValues.${legacy}`) : getFormValueLabel(value, t, isFemale);
}

export function wantsChildrenLabel(value, t) {
  if (!value) return '';
  const legacy = LEGACY_WANTS_CHILDREN[value];
  return legacy ? t(`formValues.${legacy}`) : getFormValueLabel(value, t);
}

// The interest chips both create forms offer (owner, 2026-10-30). The stored
// value is the Arabic word, in `personal_traits.hobbies` beside anything typed.
export const INTERESTS = [
  'القراءة',
  'الطبخ',
  'السفر',
  'الرياضة',
  'المشي',
  'السباحة',
  'كرة القدم',
  'الرسم',
  'التصوير',
  'الخط العربي',
  'حفظ القرآن',
  'العمل التطوعي',
  'التقنية',
  'الزراعة',
  'الخياطة والتطريز',
  'ركوب الخيل',
];

// When a member hopes to marry: a CODE (backend services/marriage_timeline.py).
export const MARRIAGE_TIMELINES = ['within_6_months', 'within_1_year', 'within_2_years', 'not_sure'];
const TIMELINE_KEY = {
  within_6_months: 'timeline6Months',
  within_1_year: 'timeline1Year',
  within_2_years: 'timeline2Years',
  not_sure: 'timelineNotSure',
};

/** The words for a stored timeline code, or '' for no answer / an unknown code. */
export function marriageTimelineLabel(code, t) {
  const key = TIMELINE_KEY[code];
  return key ? t(`formValues.${key}`) : '';
}

export default getFormValueLabel;
